/**
 * ChecklistAcceptanceConfigCard - Configuração do aceite no modelo de checklist
 *
 * - Liga/desliga o aceite, escolhe a política padrão e os "momentos de aceite".
 * - Cada momento (quem aceita + quando) pode usar um formato próprio (política:
 *   termo + assinatura/selfie/localização/CPF); sem formato => usa o padrão.
 * - Salva no rascunho; vale para vistorias após publicar. Servidor valida tudo.
 *
 * @version 1.3.0
 * RELEASE: 2026-10-13 v1.3.0 - Opção "Permitir aceite por testemunha" (allow_witness):
 *          o aceite do cliente pode ser assinado por uma testemunha (nome, CPF e
 *          nascimento obrigatórios). Desligada por padrão; vale após publicar.
 * RELEASE: 2026-10-01 v1.2.0 - Requisito "Data de nascimento" no formato de aceite.
 * RELEASE: 2026-09-27 v1.1.0 - Momentos de aceite com formato por participante.
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial (Aceites Fase 3).
 */
import { useEffect, useState } from 'react';
import { PenLine, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  PURPOSE_LABEL, ROLE_LABEL, STAGE_LABEL, useAcceptancePolicies, useSaveTemplateAcceptanceConfig,
  useTemplateAcceptanceConfig, useUpsertAcceptancePolicy, type AcceptancePolicy, type AcceptanceRole, type AcceptanceStage,
} from '@/hooks/useChecklistAcceptance';

/** Uma linha de momento de aceite. policy_id vazio = formato padrão. */
type Moment = { role: AcceptanceRole; required_stage: AcceptanceStage; required: boolean; policy_id: string };

/** Combinações papel × momento aceitas pelo servidor */
const ALLOWED: Record<AcceptanceRole, AcceptanceStage[]> = {
  executor: ['before_submit', 'before_complete'],
  reviewer: ['before_approve'],
  owner: ['before_submit', 'before_approve', 'before_complete'],
};
const DEFAULT_POLICY = '__default__';

/** Resumo das exigências de um formato */
const reqSummary = (p?: AcceptancePolicy) => {
  if (!p?.version) return '';
  const r = p.version.requirements;
  return [r.signature_drawing && 'assinatura', r.selfie && 'selfie', r.geolocation && 'localização', r.document_cpf && 'CPF', r.birth_date && 'data de nascimento']
    .filter(Boolean).join(', ') || 'somente nome';
};

export function ChecklistAcceptanceConfigCard({ templateId, requiresReview, disabled }: {
  templateId: string; requiresReview: boolean; disabled: boolean;
}) {
  const { data: config } = useTemplateAcceptanceConfig(templateId);
  const { data: policies = [] } = useAcceptancePolicies();
  const save = useSaveTemplateAcceptanceConfig(templateId);
  const [enabled, setEnabled] = useState(false);
  const [policyId, setPolicyId] = useState('');
  const [moments, setMoments] = useState<Moment[]>([]);
  // v1.3.0: aceite do cliente por testemunha
  const [allowWitness, setAllowWitness] = useState(false);
  const [policyDialog, setPolicyDialog] = useState<{ policy: AcceptancePolicy | null; target: number | 'default' } | null>(null);

  // Sincroniza com a configuração salva
  useEffect(() => {
    setEnabled(!!config?.is_enabled);
    setPolicyId(config?.policy_id ?? '');
    setAllowWitness(!!config?.allow_witness);
    setMoments(config?.participants?.length
      ? config.participants.map((p) => ({ role: p.role, required_stage: p.required_stage, required: p.required, policy_id: p.policy_id ?? '' }))
      : [{ role: 'executor', required_stage: 'before_submit', required: true, policy_id: '' }]);
  }, [config]);

  const activePolicies = policies.filter((p) => p.is_active);
  const selected = policies.find((p) => p.id === policyId);
  const stageOk = (s: AcceptanceStage) => requiresReview || s !== 'before_approve';
  const setMoment = (i: number, patch: Partial<Moment>) => setMoments((ms) => ms.map((m, idx) => {
    if (idx !== i) return m;
    const next = { ...m, ...patch };
    // Troca de papel: ajusta o momento para um permitido
    if (!ALLOWED[next.role].includes(next.required_stage)) next.required_stage = ALLOWED[next.role].find(stageOk) ?? ALLOWED[next.role][0];
    return next;
  }));
  const duplicated = moments.some((m, i) => moments.findIndex((x) => x.role === m.role && x.required_stage === m.required_stage) !== i);
  const invalid = moments.some((m) => !stageOk(m.required_stage) || (m.role === 'reviewer' && !requiresReview));

  const handleSave = () => save.mutate(enabled
    ? {
      is_enabled: true, policy_id: policyId, allow_witness: allowWitness,
      participants: moments.map(({ role, required_stage, required, policy_id }) => ({ role, required_stage, required, ...(policy_id ? { policy_id } : {}) })),
    }
    : { is_enabled: false });

  return (
    <FormSectionCard title="Aceite (assinatura na tela)" icon={PenLine}>
      <div className="flex items-center justify-between gap-3">
        <Label className="font-normal">Exigir aceite nesta vistoria</Label>
        <Switch checked={enabled} disabled={disabled} onCheckedChange={setEnabled} />
      </div>

      {enabled && (
        <>
          <div className="space-y-1.5">
            <Label>Formato padrão (termo e evidências)</Label>
            <div className="flex gap-2">
              <Select value={policyId} disabled={disabled} onValueChange={setPolicyId}>
                <SelectTrigger><SelectValue placeholder="Selecione a política" /></SelectTrigger>
                <SelectContent>
                  {activePolicies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {PURPOSE_LABEL[p.purpose] ?? p.purpose}</SelectItem>)}
                </SelectContent>
              </Select>
              {!disabled && (
                <>
                  {selected && !selected.is_system && (
                    <Button type="button" size="icon" variant="outline" title="Editar política" onClick={() => setPolicyDialog({ policy: selected, target: 'default' })}><Pencil className="h-4 w-4" /></Button>
                  )}
                  <Button type="button" size="icon" variant="outline" title="Nova política" onClick={() => setPolicyDialog({ policy: null, target: 'default' })}><Plus className="h-4 w-4" /></Button>
                </>
              )}
            </div>
            {selected?.version && <p className="text-xs text-muted-foreground">Exige: {reqSummary(selected)}</p>}
          </div>

          {/* v1.3.0: aceite do cliente por testemunha (nome, CPF e nascimento obrigatórios) */}
          <div className="space-y-1 rounded-md border px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <Label className="font-normal">Permitir aceite por testemunha</Label>
              <Switch checked={allowWitness} disabled={disabled} onCheckedChange={setAllowWitness} />
            </div>
            <p className="text-xs text-muted-foreground">
              Quando ligado, quem coleta o aceite do cliente escolhe entre o titular (dados do proprietário)
              ou uma testemunha presente no local, que informa nome, CPF e data de nascimento.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Momentos de aceite</Label>
              {!disabled && moments.length < 6 && (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setMoments((ms) => [...ms, { role: 'owner', required_stage: 'before_submit', required: true, policy_id: '' }])}>
                  <Plus className="mr-1 h-3.5 w-3.5" />Adicionar momento
                </Button>
              )}
            </div>
            {moments.map((m, i) => {
              const pol = policies.find((p) => p.id === (m.policy_id || policyId));
              return (
                <div key={i} className="space-y-1 rounded-md border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={m.required_stage} disabled={disabled} onValueChange={(v) => setMoment(i, { required_stage: v as AcceptanceStage })}>
                      <SelectTrigger className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ALLOWED[m.role].map((s) => <SelectItem key={s} value={s} disabled={!stageOk(s)}>{STAGE_LABEL[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={m.role} disabled={disabled} onValueChange={(v) => setMoment(i, { role: v as AcceptanceRole })}>
                      <SelectTrigger className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(Object.keys(ROLE_LABEL) as AcceptanceRole[]).map((r) => (
                          <SelectItem key={r} value={r} disabled={r === 'reviewer' && !requiresReview}>{ROLE_LABEL[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={m.policy_id || DEFAULT_POLICY} disabled={disabled} onValueChange={(v) => setMoment(i, { policy_id: v === DEFAULT_POLICY ? '' : v })}>
                      <SelectTrigger className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DEFAULT_POLICY}>Formato padrão</SelectItem>
                        {activePolicies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {!disabled && (
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" title="Novo formato para este momento"
                        onClick={() => setPolicyDialog({ policy: null, target: i })}><Plus className="h-4 w-4" /></Button>
                    )}
                    <label className="flex items-center gap-1.5 text-xs">
                      <Switch checked={m.required} disabled={disabled} onCheckedChange={(v) => setMoment(i, { required: v })} />Obrigatório
                    </label>
                    {!disabled && moments.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="ml-auto h-8 w-8" title="Remover momento"
                        onClick={() => setMoments((ms) => ms.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                  {pol?.version && <p className="text-xs text-muted-foreground">Exige: {reqSummary(pol)}</p>}
                </div>
              );
            })}
            {duplicated && <p className="text-xs text-destructive">Há momentos repetidos (mesmo participante e etapa).</p>}
            {!requiresReview && <p className="text-xs text-muted-foreground">Ative a revisão no modelo para usar momentos da revisão.</p>}
          </div>
        </>
      )}

      {!disabled && (
        <div className="flex justify-end">
          <Button type="button" size="sm" variant="outline" className="h-7" onClick={handleSave}
            disabled={save.isPending || (enabled && (!policyId || moments.length === 0 || duplicated || invalid))}>
            <Save className="mr-1 h-4 w-4" />{save.isPending ? 'Salvando...' : 'Salvar aceite'}
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">O aceite vale para as vistorias criadas após publicar uma nova versão do modelo.</p>

      {policyDialog && (
        <PolicyDialog policy={policyDialog.policy} onClose={() => setPolicyDialog(null)} onSaved={(id) => {
          // Novo formato vai para o padrão ou para o momento que o criou
          if (policyDialog.target === 'default') setPolicyId(id); else setMoment(policyDialog.target, { policy_id: id });
          setPolicyDialog(null);
        }} />
      )}
    </FormSectionCard>
  );
}

/** Criação/edição de política do tenant. O servidor versiona termo e requisitos. */
function PolicyDialog({ policy, onClose, onSaved }: { policy: AcceptancePolicy | null; onClose: () => void; onSaved: (id: string) => void }) {
  const upsert = useUpsertAcceptancePolicy();
  const [f, setF] = useState({
    name: policy?.name ?? '',
    purpose: policy?.purpose ?? 'declaration',
    terms_title: policy?.terms?.title ?? 'Termo de vistoria',
    terms_body: policy?.terms?.body ?? '',
    terms_declaration: policy?.terms?.declaration ?? 'Declaro que as informações registradas nesta vistoria são verdadeiras.',
    signature_drawing: policy?.version?.requirements.signature_drawing ?? true,
    selfie: policy?.version?.requirements.selfie ?? false,
    geolocation: policy?.version?.requirements.geolocation ?? true,
    document_cpf: policy?.version?.requirements.document_cpf ?? false,
    // Exigir data de nascimento do signatário
    birth_date: policy?.version?.requirements.birth_date ?? false,
  });
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.name.trim().length >= 3 && f.terms_title.trim().length >= 3 && f.terms_body.trim().length >= 10 && f.terms_declaration.trim().length >= 10;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="text-base">{policy ? 'Editar política de aceite' : 'Nova política de aceite'}</DialogTitle>
          <DialogDescription className="text-xs">Alterações geram nova versão; aceites já registrados mantêm a versão original.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={f.name} maxLength={120} onChange={(e) => set('name', e.target.value.replace(/[<>]/g, ''))} /></div>
            <div className="space-y-1.5"><Label>Finalidade</Label>
              <Select value={f.purpose} onValueChange={(v) => set('purpose', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PURPOSE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label>Título do termo</Label><Input value={f.terms_title} maxLength={150} onChange={(e) => set('terms_title', e.target.value.replace(/[<>]/g, ''))} /></div>
          <div className="space-y-1.5"><Label>Texto do termo</Label><Textarea rows={6} value={f.terms_body} maxLength={20000} onChange={(e) => set('terms_body', e.target.value.replace(/[<>]/g, ''))} /></div>
          <div className="space-y-1.5"><Label>Declaração a confirmar</Label><Textarea rows={2} value={f.terms_declaration} maxLength={1000} onChange={(e) => set('terms_declaration', e.target.value.replace(/[<>]/g, ''))} /></div>
          <div className="grid gap-2 sm:grid-cols-2">
            {([['signature_drawing', 'Assinatura desenhada'], ['selfie', 'Selfie'], ['geolocation', 'Localização'], ['document_cpf', 'CPF'], ['birth_date', 'Data de nascimento']] as const).map(([k, l]) => (
              <label key={k} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">{l}<Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} /></label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t px-6 py-3">
          <Button size="sm" variant="outline" className="h-7" onClick={onClose}><X className="mr-1 h-4 w-4" />Cancelar</Button>
          <Button size="sm" className="h-7" disabled={!valid || upsert.isPending} onClick={() => upsert.mutate({
            id: policy?.id,
            data: {
              name: f.name.trim(), purpose: f.purpose, terms_title: f.terms_title.trim(), terms_body: f.terms_body.trim(),
              terms_declaration: f.terms_declaration.trim(),
              requirements: { signature_drawing: f.signature_drawing, selfie: f.selfie, geolocation: f.geolocation, document_cpf: f.document_cpf, birth_date: f.birth_date },
            },
          }, { onSuccess: (id) => onSaved(id) })}>
            <Save className="mr-1 h-4 w-4" />Salvar política
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
